import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
  Label,
} from "recharts";
import { ChevronUp, ChevronDown } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FilterButton } from "./components/Filter/FilterButton";
import FilterPanel from "./components/Filter/FilterPanel";
import LineChart from "./components/LineChart/LineChart";
import CustomDropdown from "./components/extras/CustomDropdown";
import useClickOutside from "./hooks/useClickOutside";

// function DateRangePicker({ dateRange, setDateRange }) {
//   return (
//     <div className="flex space-x-2">
//       <div className="flex-1">
//         <label className="block text-xs text-gray-400 mb-1">From:</label>
//         <DatePicker
//           selected={dateRange[0]}
//           onChange={(date) => {
//             if (date && dateRange[1] && date > dateRange[1]) {
//               return;
//             }
//             setDateRange([date, dateRange[1]]);
//           }}
//           selectsStart
//           startDate={dateRange[0]}
//           endDate={dateRange[1]}
//           className="w-44 glass-effect border border-gray-600 rounded-full px-3 py-2 text-[#EDEDEDE5] text-sm focus:outline-none focus:border-white bg-gray-800"
//           popperClassName="dark-theme-datepicker"
//           popperPlacement="top-start"
//           dateFormat="dd-MM-yyyy"
//           placeholderText="Select start date"
//         />
//       </div>
//       <div className="flex-1">
//         <label className="block text-xs text-gray-400 mb-1">To:</label>
//         <DatePicker
//           selected={dateRange[1]}
//           onChange={(date) => {
//             if (date && dateRange[0] && date < dateRange[0]) {
//               return;
//             }
//             setDateRange([dateRange[0], date]);
//           }}
//           selectsEnd
//           startDate={dateRange[0]}
//           endDate={dateRange[1]}
//           className="w-44 glass-effect border border-gray-600 rounded-full px-3 py-2 text-[#EDEDEDE5] text-sm focus:outline-none focus:border-white bg-gray-800"
//           popperClassName="dark-theme-datepicker"
//           popperPlacement="top-start"
//           dateFormat="dd-MM-yyyy"
//           placeholderText="Select end date"
//         />
//       </div>
//       <style jsx>{`
//         .dark-theme-datepicker .react-datepicker {
//           background-color: #1f2937;
//           border: 1px solid #4b5563;
//           border-radius: 0.5rem;
//           color: #e5e7eb;
//         }
//         .dark-theme-datepicker .react-datepicker__header {
//           background-color: #374151;
//           border-bottom: 1px solid #4b5563;
//           color: #e5e7eb;
//         }
//         .dark-theme-datepicker .react-datepicker__current-month,
//         .dark-theme-datepicker .react-datepicker__day-name,
//         .dark-theme-datepicker .react-datepicker__day {
//           color: #e5e7eb;
//         }
//         .dark-theme-datepicker .react-datepicker__day:hover {
//           background-color: #6d42b9;
//           border-radius: 1rem;
//         }
//         .dark-theme-datepicker .react-datepicker__day--selected,
//         .dark-theme-datepicker .react-datepicker__day--in-range {
//           background-color: #6d42b9;
//           color: #ffffff;
//           border-radius: 1rem;
//         }
//         .dark-theme-datepicker .react-datepicker__day--today {
//           background-color: #10b981;
//           color: #ffffff;
//           border-radius: 1rem;
//         }
//         .dark-theme-datepicker .react-datepicker__day--outside-month {
//           color: #6b7280;
//         }
//         .dark-theme-datepicker .react-datepicker__navigation-icon::before {
//           border-color: #e5e7eb;
//         }
//         .dark-theme-datepicker .react-datepicker__triangle {
//           border-bottom-color: #374151;
//         }
//         .dark-theme-datepicker .react-datepicker__triangle::before {
//           border-bottom-color: #4b5563;
//         }
//       `}</style>
//     </div>
//   );
// }

const UserFilterDropdown = ({
  tempSelectedUser,
  setTempSelectedUser,
  userOptions,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <div className="w-82">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full flex justify-between items-center px-3 py-2 bg-[#0a1733] border 
          ${disabled 
            ? "border-[#3a2c50] opacity-50 cursor-not-allowed" 
            : "border-[#5A4479] hover:border-[#7E57C2]"} 
          rounded-md text-[#D3B4FF] text-sm transition-colors`}
      >
        <span className="truncate">
          {tempSelectedUser === "all" ? "All Users" : tempSelectedUser || "Select user"}
        </span>

        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-[#D3B4FF]" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#D3B4FF]" />
        )}
      </button>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full left-0 bg-[#0a1733] border border-[#5A4479] rounded-md shadow-lg max-h-60 overflow-y-auto">
          {userOptions.map((option) => (
            <div
              key={option.value}
              onClick={() => {
                setTempSelectedUser(option.value);
                setIsOpen(false);
              }}
              className="px-3 py-2 text-sm text-[#D3B4FF] hover:bg-[#2A2A2A] cursor-pointer"
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AccLvlGraph = ({ userData, interviewData, jobPostingsData, resumeParseData }) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [selectedUser, setSelectedUser] = useState("all");
  const [tempSelectedUser, setTempSelectedUser] = useState(selectedUser);
  const [filterCount, setFilterCount] = useState(0);
  const [activeChart, setActiveChart] = useState("usage");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const filterRef = useRef(null);
  const buttonRef = useRef(null);
  const [selectedDatePeriod, setSelectedDatePeriod] = useState("custom");
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);

  const colorPalette = ["#3B82F6", "#EF4444", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#22D3EE"];

  const chartOptions = [
    { value: "usage", label: "User Login" },
    { value: "interview", label: "Interviews Scheduled" },
    { value: "job_postings", label: "Jobs Created" },
    { value: "resume_parse", label: "Resumes Parsed" },
  ];

  const userOptions = useMemo(() => {
    const options = [{ value: "all", label: "All" }];
    if (userData?.org_admin_name) {
      options.push({ value: userData.org_admin_name, label: userData.org_admin_name });
    }
    if (userData?.hrs && Array.isArray(userData.hrs)) {
      userData.hrs.forEach((hr) => {
        if (hr.name) {
          options.push({ value: hr.name, label: hr.name });
        }
      });
    }
    return options;
  }, [userData]);

  // Helper: Get start/end dates, default to last 7 days
  const getDateRangeBounds = (dateRange) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    let startDate = new Date(today);
    startDate.setDate(today.getDate() - 6);
    startDate.setHours(0, 0, 0, 0);
    let endDate = today;

    if (dateRange[0] && dateRange[1]) {
      startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(dateRange[1]);
      endDate.setHours(23, 59, 59, 999);
    }

    return { startDate, endDate };
  };

  // Helper: Generate date keys and formatted labels
  const generateDateKeys = (startDate, endDate) => {
    const toISTDateString = (date) => {
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istDate = new Date(date.getTime() + istOffset);
      const year = istDate.getFullYear();
      const month = String(istDate.getMonth() + 1).padStart(2, "0");
      const day = String(istDate.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const formatISTDate = (date) => {
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istDate = new Date(date.getTime() + istOffset);
      const day = String(istDate.getDate()).padStart(2, "0");
      const month = String(istDate.getMonth() + 1).padStart(2, "0");
      const year = istDate.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const allDates = [];
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      allDates.push(new Date(d));
    }
    const allKeys = new Set(allDates.map((d) => toISTDateString(d)));
    const sortedKeys = Array.from(allKeys).sort((a, b) => new Date(a) - new Date(b));
    const formattedKeys = sortedKeys.map((date) => formatISTDate(new Date(date)));

    return { allKeys, sortedKeys, formattedKeys, toISTDateString };
  };

  // Helper: Count timestamps per day
  const countTimestamps = (timestamps, startDate, endDate, allKeys, toISTDateString, filterFn = () => true) => {
    const counts = {};
    allKeys.forEach((key) => (counts[key] = 0));
    timestamps.forEach((ts) => {
      const effectiveTs = ts.date ? ts.date : ts; // For resume entries which are objects
      if (filterFn(ts) && effectiveTs) {
        const date = new Date(effectiveTs);
        if (!isNaN(date.getTime())) {
          const istDate = new Date(date.getTime());
          if (istDate >= startDate && istDate <= endDate) {
            const key = toISTDateString(istDate);
            counts[key]++;
          }
        }
      }
    });
    return counts;
  };

  useClickOutside([filterRef, buttonRef], () => {
    setIsFilterOpen(false);
    setIsDateDropdownOpen(false);
  });

  useEffect(() => {
    const today = new Date();
    let startDate = null;
    switch (selectedDatePeriod) {
      case "yesterday":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 1);
        setTempDateRange([startDate, startDate]);
        break;
      case "weekly":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 6);
        setTempDateRange([startDate, today]);
        break;
      case "monthly":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 29);
        setTempDateRange([startDate, today]);
        break;
      case "yearly":
        startDate = new Date(today);
        startDate.setFullYear(today.getFullYear() - 1);
        setTempDateRange([startDate, today]);
        break;
      case "custom":
      default:
        break;
    }
  }, [selectedDatePeriod]);

  useEffect(() => {
    const count =
      (dateRange[0] || dateRange[1] ? 1 : 0) +
      (activeChart !== "job_postings" && selectedUser !== "all" ? 1 : 0);
    setFilterCount(count);
  }, [dateRange, selectedUser, activeChart]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setTempDateRange(dateRange);
    setTempSelectedUser(selectedUser);
  }, [dateRange, selectedUser]);

  const handleApplyFilter = () => {
    setDateRange(tempDateRange);
    setSelectedUser(tempSelectedUser);
    setIsFilterOpen(false);
  };

  const handleResetFilter = () => {
    setTempDateRange([null, null]);
    setDateRange([null, null]);
    setSelectedDatePeriod("custom");
    setTempSelectedUser("all");
    setSelectedUser("all");
    setIsFilterOpen(false);
  };

  const handleGraphClick = () => {
    let dataToShow;
    if (activeChart === "usage") {
      dataToShow = userData;
    } else if (activeChart === "interview") {
      dataToShow = interviewData;
    } else if (activeChart === "job_postings") {
      dataToShow = jobPostingsData;
    } else if (activeChart === "resume_parse") {
      dataToShow = resumeParseData;
    }
    setModalData(dataToShow);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalData(null);
  };

  const processLoginHistoryData = useMemo(() => {
    if (!userData?.org_admin_login_history) return { xAxisData: [], series: [] };

    const { startDate, endDate } = getDateRangeBounds(dateRange);
    const { allKeys, sortedKeys, formattedKeys, toISTDateString } = generateDateKeys(startDate, endDate);

    const loginData = {};
    const adminName = userData.org_admin_name || "Admin";
    const filterFn = (ts) => true; // No special filter for logins

    if (selectedUser === "all" || selectedUser === adminName) {
      loginData[adminName] = countTimestamps(
        userData.org_admin_login_history || [],
        startDate,
        endDate,
        allKeys,
        toISTDateString,
        filterFn
      );
    }

    if (userData.hrs && Array.isArray(userData.hrs)) {
      userData.hrs.forEach((hr) => {
        if (hr.name && (selectedUser === "all" || selectedUser === hr.name)) {
          loginData[hr.name] = countTimestamps(
            hr.login_history || [],
            startDate,
            endDate,
            allKeys,
            toISTDateString,
            filterFn
          );
        }
      });
    }

    const series = Object.entries(loginData).map(([name, data], index) => ({
      data: sortedKeys.map((key) => data[key] || 0),
      label: name,
      showMark: true,
      valueFormatter: (value) => `${value} logins`,
      color: colorPalette[index % colorPalette.length],
    }));

    return { xAxisData: formattedKeys, series };
  }, [userData, dateRange, selectedUser]);

  const processInterviewData = useMemo(() => {
    if (!interviewData || !interviewData.hr_usage || !Array.isArray(interviewData.hr_usage)) {
      return { xAxisData: [], series: [] };
    }

    const { startDate, endDate } = getDateRangeBounds(dateRange);
    const { allKeys, sortedKeys, formattedKeys, toISTDateString } = generateDateKeys(startDate, endDate);

    const interviewDataObj = {};
    const adminName = interviewData.org_admin_name || "Admin";
    const filterFn = (ts) => ts !== "N/A";

    if (selectedUser === "all" || selectedUser === adminName) {
      interviewDataObj[adminName] = countTimestamps(
        interviewData.org_admin_invited_dates || [],
        startDate,
        endDate,
        allKeys,
        toISTDateString,
        filterFn
      );
    }

    interviewData.hr_usage.forEach((hr) => {
      if (hr.name && (selectedUser === "all" || selectedUser === hr.name)) {
        interviewDataObj[hr.name] = countTimestamps(
          hr.invited_dates || [],
          startDate,
          endDate,
          allKeys,
          toISTDateString,
          filterFn
        );
      }
    });

    const series = Object.entries(interviewDataObj).map(([name, data], index) => ({
      data: sortedKeys.map((key) => data[key] || 0),
      label: name,
      showMark: true,
      valueFormatter: (value) => `${value} interviews`,
      color: colorPalette[index % colorPalette.length],
    }));

    return { xAxisData: formattedKeys, series };
  }, [interviewData, dateRange, selectedUser]);

  const processJobPostingsData = useMemo(() => {
    if (!jobPostingsData || !jobPostingsData.job_descriptions || !Array.isArray(jobPostingsData.job_descriptions)) {
      return { xAxisData: [], series: [] };
    }

    const { startDate, endDate } = getDateRangeBounds(dateRange);
    const { allKeys, sortedKeys, formattedKeys, toISTDateString } = generateDateKeys(startDate, endDate);

    const filterFn = (job) => job.postingDate !== "N/A";
    const timestamps = jobPostingsData.job_descriptions.filter(filterFn).map((job) => job.postingDate);

    const jobData = {
      "Job Postings": countTimestamps(
        timestamps,
        startDate,
        endDate,
        allKeys,
        toISTDateString,
        () => true // Already filtered
      ),
    };

    const series = [
      {
        data: sortedKeys.map((key) => jobData["Job Postings"][key] || 0),
        label: "Job Postings",
        showMark: true,
        valueFormatter: (value) => `${value} postings`,
        color: "#F59E0B",
      },
    ];

    return { xAxisData: formattedKeys, series };
  }, [jobPostingsData, dateRange]);

  const processResumeParseData = useMemo(() => {
    if (!resumeParseData || !resumeParseData.hr_resume_parsing || !Array.isArray(resumeParseData.hr_resume_parsing)) {
      return { xAxisData: [], series: [] };
    }

    const { startDate, endDate } = getDateRangeBounds(dateRange);
    const { allKeys, sortedKeys, formattedKeys, toISTDateString } = generateDateKeys(startDate, endDate);

    const resumeDataObj = {};
    const adminName = resumeParseData.org_admin_name || "Admin";
    const filterFn = (entry) => entry && entry.date && (entry.type === "created_date" || entry.type === "bulk_upload_date");

    if (selectedUser === "all" || selectedUser === adminName) {
      resumeDataObj[adminName] = countTimestamps(
        resumeParseData.org_admin_resume_parsing_dates || [],
        startDate,
        endDate,
        allKeys,
        toISTDateString,
        filterFn
      );
    }

    resumeParseData.hr_resume_parsing.forEach((hr) => {
      if (hr.name && (selectedUser === "all" || selectedUser === hr.name)) {
        resumeDataObj[hr.name] = countTimestamps(
          hr.resume_parsing_dates || [],
          startDate,
          endDate,
          allKeys,
          toISTDateString,
          filterFn
        );
      }
    });

    const series = Object.entries(resumeDataObj).map(([name, data], index) => ({
      data: sortedKeys.map((key) => data[key] || 0),
      label: name,
      showMark: true,
      valueFormatter: (value) => `${value} resumes`,
      color: colorPalette[index % colorPalette.length],
    }));

    return { xAxisData: formattedKeys, series };
  }, [resumeParseData, dateRange, selectedUser]);

  const activeFilters = useMemo(() => {
    const filters = [];

    if (selectedDatePeriod !== "custom" || dateRange[0] || dateRange[1]) {
      let label = "";
      if (selectedDatePeriod === "yesterday") label = "Yesterday";
      else if (selectedDatePeriod === "weekly") label = "Last 7 Days";
      else if (selectedDatePeriod === "monthly") label = "Last 30 Days";
      else if (selectedDatePeriod === "yearly") label = "Last Year";
      else if (dateRange[0] && dateRange[1]) {
        const format = (d) =>
          d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
        label = `${format(dateRange[0])} - ${format(dateRange[1])}`;
      } else {
        label = "Custom Date";
      }

      filters.push({
        label: `Date: ${label}`,
        onRemove: () => {
          setTempDateRange([null, null]);
          setDateRange([null, null]);
          setSelectedDatePeriod("custom");
        },
      });
    }

    if (activeChart !== "job_postings" && selectedUser !== "all") {
      filters.push({
        label: `User: ${selectedUser}`,
        onRemove: () => {
          setTempSelectedUser("all");
          setSelectedUser("all");
        },
      });
    }

    return filters;
  }, [dateRange, selectedUser, selectedDatePeriod, activeChart]);

  const preparedDataSources = useMemo(() => {
    let xAxisData = [];
    let series = [];

    if (activeChart === "usage") {
      ({ xAxisData, series } = processLoginHistoryData);
    } else if (activeChart === "interview") {
      ({ xAxisData, series } = processInterviewData);
    } else if (activeChart === "job_postings") {
      ({ xAxisData, series } = processJobPostingsData);
    } else if (activeChart === "resume_parse") {
      ({ xAxisData, series } = processResumeParseData);
    }

    if (xAxisData.length === 0 || series.length === 0) return [];

    return series.map((s) => {
      const data = xAxisData.map((dateStr, i) => {
        const [day, month, year] = dateStr.split("-");
        const isoDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        return {
          date: isoDate,
          count: s.data[i] || 0,
        };
      });

      return {
        key: s.label,
        data,
        color: s.color,
      };
    });
  }, [
    activeChart,
    processLoginHistoryData,
    processInterviewData,
    processJobPostingsData,
    processResumeParseData,
  ]);

  return (
    <div
      className="glass-effect p-4 rounded-2xl border border-blue-500/30"
      style={{
        width: "100%",
        maxWidth: "750px",
        height: "353px",
        borderRadius: "10px",
        background:
          "linear-gradient(252.82deg, rgba(217, 217, 217, 0.025) 25.41%, rgba(115, 108, 207, 0.005) 115.2%)",
        backdropFilter: "blur(75px)",
        border: "0px solid",
        position: "relative",
      }}
    >
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h3
            className="text-lg font-medium"
            style={{
              color: "#D3B4FF",
              fontFamily: "Inter",
              fontWeight: 300,
              fontStyle: "Light",
              fontSize: "16px",
              lineHeight: "100%",
              letterSpacing: "1%",
              opacity: 1,
              transform: "rotate(0deg)",
              whiteSpace: "nowrap",
              marginTop: "-10px",
            }}
          >
            Usage Frequency
          </h3>
          <div className="w-52">
            <CustomDropdown
              options={chartOptions}
              value={activeChart}
              onChange={setActiveChart}
              className="w-full"
              placeholder="Select chart type"
            />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative -mt-2">
            <FilterButton
              filterCount={filterCount}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              onClear={handleResetFilter}
            />
            <FilterPanel
              ref={filterRef}
              isOpen={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
              activeFilters={activeFilters}
              selectedPeriod={selectedDatePeriod}
              onPeriodChange={setSelectedDatePeriod}
              tempDateRange={tempDateRange}
              onTempDateChange={setTempDateRange}
              onApply={handleApplyFilter}
              onReset={handleResetFilter}
              showCustomPicker={true}
              position="bottom-right"
              dropdownOpen={isDateDropdownOpen}
              setDropdownOpen={setIsDateDropdownOpen}
              extraContent={
                <div className="mb-4 flex flex-col justify-center w-[100%]">
                  <label className="block text-[#CCCCCC] text-sm font-medium mb-2">
                    User
                  </label>

                  <UserFilterDropdown
                    tempSelectedUser={tempSelectedUser}
                    setTempSelectedUser={setTempSelectedUser}
                    userOptions={userOptions}
                    disabled={activeChart === "job_postings"}
                  />
                </div>
              }
            >
            </FilterPanel>
          </div>
        </div>
      </div>
      <div className="h-[260px] -mt-2 cursor-pointer -ml-32" onClick={handleGraphClick}>
        <LineChart
          dataSources={preparedDataSources}
          selectedFeature="All"
          chartTheme={{}}
          height={260}
          width="120%"
          noDataMessage="No data available for selected filters"
        />
      </div>
      {preparedDataSources.length > 0 && (
        <div className="flex flex-wrap justify-center -mt-8 gap-6 text-[#CCCCCC] text-sm">
          {preparedDataSources.map((source, i) => (
            <div key={source.key} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: source.color || colorPalette[i % colorPalette.length] }}
              />
              <span>{source.key}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccLvlGraph;